import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from './Modal';
import Button from './Button';
import { Employee, NameDisplayFormat, EmployeeRole } from '../../shared/types.js';
import { getDateRangeForPreset, DateRangePreset, DATE_RANGE_PRESET_OPTIONS, DATE_RANGE_PRESET_ORDER, formatEmployeeName } from '../utils.js';
import { useTimeClock } from '../hooks/useTimeClock';

export interface ReportCriteria {
    employeeIds: string[];
    startDate: string;
    endDate: string;
}

interface ReportCriteriaModalProps {
    isOpen: boolean;
    onClose: () => void;
    onGenerate: (criteria: ReportCriteria) => void;
    employees: Employee[];
    title?: string;
    initialCriteria?: ReportCriteria | null;
    isEmployeeView?: boolean;
}

const ReportCriteriaModal: React.FC<ReportCriteriaModalProps> = ({ isOpen, onClose, onGenerate, employees, title = "Select Employees & Date Range", initialCriteria = null, isEmployeeView = false }) => {
    const { payrollSettings } = useTimeClock();
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
    const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('this_period');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const sortedEmployees = useMemo(() => [...employees].sort((a,b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '')), [employees]);
    const activeEmployees = useMemo(() => employees.filter(e => e.role !== EmployeeRole.INACTIVE), [employees]);
    const inactiveEmployeeIds = useMemo(() => new Set(employees.filter(e => e.role === EmployeeRole.INACTIVE).map(e => e.id)), [employees]);
    const [showInactive, setShowInactive] = useState(false);
    const visibleEmployees = useMemo(() => {
        if (showInactive) return sortedEmployees;
        return sortedEmployees.filter(e => e.role !== EmployeeRole.INACTIVE);
    }, [sortedEmployees, showInactive]);
    useEffect(() => {
        if (isOpen) {
            if (initialCriteria) {
                setSelectedEmployeeIds(initialCriteria.employeeIds);
                setStartDate(initialCriteria.startDate);
                setEndDate(initialCriteria.endDate);
                let matchedPreset: DateRangePreset = 'custom';
                for (const presetKey of Object.keys(DATE_RANGE_PRESET_OPTIONS) as DateRangePreset[]) {
                    if (presetKey === 'custom') continue;
                    const { start, end } = getDateRangeForPreset(presetKey, payrollSettings);
                    if (start === initialCriteria.startDate && end === initialCriteria.endDate) {
                        matchedPreset = presetKey;
                        break;
                    }
                }
                setDateRangePreset(matchedPreset);
            } else {
                setSelectedEmployeeIds(activeEmployees.map(e => e.id));
                const { start, end } = getDateRangeForPreset('this_period', payrollSettings);
                setStartDate(start);
                setEndDate(end);
                setDateRangePreset('this_period');
            }
            const hasInactiveSelection = (initialCriteria ? initialCriteria.employeeIds : activeEmployees.map(e => e.id)).some(id => inactiveEmployeeIds.has(id));
            setShowInactive(hasInactiveSelection);
        }
    }, [isOpen, employees, payrollSettings, initialCriteria, activeEmployees, inactiveEmployeeIds]);
    useEffect(() => {
        if (dateRangePreset !== 'custom') {
            const { start, end } = getDateRangeForPreset(dateRangePreset, payrollSettings);
            setStartDate(start);
            setEndDate(end);
        }
    }, [dateRangePreset, payrollSettings]);
    const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStartDate(e.target.value);
        setDateRangePreset('custom');
    };
    const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEndDate(e.target.value);
        setDateRangePreset('custom');
    };
    const handleSelectEmployee = (id: string) => {
        setSelectedEmployeeIds(prev =>
            prev.includes(id) ? prev.filter(empId => empId !== id) : [...prev, id]
        );
    };
    const handleSelectAll = () => {
        const visibleIds = visibleEmployees.map(e => e.id);
        setSelectedEmployeeIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    };
    const handleSelectNone = () => setSelectedEmployeeIds([]);
    const handleGenerateClick = useCallback(() => {
        if (selectedEmployeeIds.length === 0) {
            alert('Please select at least one employee.');
            return;
        }
        onGenerate({
            employeeIds: selectedEmployeeIds,
            startDate,
            endDate,
        });
    }, [selectedEmployeeIds, startDate, endDate, onGenerate]);
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                handleGenerateClick();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handleGenerateClick]);
    const formInputClasses = "block w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary";
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            <div className={`flex flex-col ${!isEmployeeView && 'md:flex-row'} gap-6 text-gray-900 dark:text-white`}>
                {!isEmployeeView && (
                    <div className="md:w-2/5 border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-gray-50 dark:bg-gray-900/50">
                        <h3 className="text-lg font-semibold text-center mb-2 border-b border-gray-200 dark:border-gray-700 pb-1">Employees</h3>
                        <div className="h-64 overflow-y-auto pr-2">
                            {visibleEmployees.map(emp => (
                                <label key={emp.id} className="flex items-center gap-3 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedEmployeeIds.includes(emp.id)}
                                        onChange={() => handleSelectEmployee(emp.id)}
                                        className="bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary"
                                    />
                                    <span className="flex items-center gap-2">
                                        {formatEmployeeName(emp, NameDisplayFormat.LAST_FIRST)}
                                        {emp.role === EmployeeRole.INACTIVE && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">(Inactive)</span>
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className="flex justify-around mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                            <Button type="button" size="sm" variant="ghost" onClick={handleSelectAll}>All</Button>
                            <Button type="button" size="sm" variant="ghost" onClick={handleSelectNone}>None</Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => setShowInactive(prev => !prev)}
                            >
                                {showInactive ? 'Hide Inactive' : 'Show Inactive'}
                            </Button>
                        </div>
                    </div>
                )}
                <div className={`${isEmployeeView ? 'w-full' : 'md:w-3/5'} space-y-6`}>
                    <fieldset className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
                        <legend className="px-2 font-semibold">Date Range</legend>
                        <div className="space-y-4">
                            <select value={dateRangePreset} onChange={e => setDateRangePreset(e.target.value as DateRangePreset)} className={formInputClasses}>
                                {DATE_RANGE_PRESET_ORDER.map((key) => (
                                    <option key={key} value={key}>{DATE_RANGE_PRESET_OPTIONS[key]}</option>
                                ))}
                            </select>
                            <div className="flex items-center gap-4">
                                <label htmlFor="report-from-date" className="w-16">From:</label>
                                <input id="report-from-date" type="date" value={startDate} onChange={handleStartDateChange} required className={formInputClasses}/>
                            </div>
                            <div className="flex items-center gap-4">
                                <label htmlFor="report-to-date" className="w-16">To:</label>
                                <input id="report-to-date" type="date" value={endDate} onChange={handleEndDateChange} required className={formInputClasses}/>
                            </div>
                        </div>
                    </fieldset>
                </div>
            </div>
            <div className="flex justify-end gap-4 pt-6 mt-4 border-t border-gray-200 dark:border-gray-700">
                <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                <Button type="button" onClick={handleGenerateClick}>OK</Button>
            </div>
        </Modal>
    );
};

export default ReportCriteriaModal;
